import type { IOptions } from "sanitize-html";
import sanitizeHtml from "sanitize-html";
import { FUNDING_APPEAL_CONTACT_FORM_ATTR } from "./fundingAppealLandingMarkers";

/** Designer exports `style` blocks and optional Google Fonts `link` tags (non-font links filtered below). */
const ALLOWED_TAGS = [
  "p",
  "br",
  "a",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "div",
  "span",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "hr",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "caption",
  "col",
  "colgroup",
  "img",
  "section",
  "article",
  "header",
  "footer",
  "main",
  "cite",
  "figure",
  "figcaption",
  "center",
  "style",
  "link",
];

const PRESENTATION_ATTRS = [
  "class",
  "style",
  "id",
  "align",
  "valign",
  "width",
  "height",
  "border",
  "cellpadding",
  "cellspacing",
  "bgcolor",
  "role",
];

const ALLOWED_ATTRIBUTES: IOptions["allowedAttributes"] = {
  "*": PRESENTATION_ATTRS,
  div: [...PRESENTATION_ATTRS, FUNDING_APPEAL_CONTACT_FORM_ATTR, "data-submit-label"],
  a: ["href", "target", "rel"],
  img: ["src", "srcset", "alt", "title", "width", "height", "loading"],
  td: ["colspan", "rowspan"],
  th: ["colspan", "rowspan"],
  style: ["type", "media"],
  link: ["rel", "href", "type", "crossorigin", "media"],
};

const sanitizeOptions: IOptions = {
  allowedTags: ALLOWED_TAGS,
  allowedAttributes: ALLOWED_ATTRIBUTES,
  /** `<style>` is XSS-sensitive; allowed only for moderated builder HTML (see `allowVulnerableTags`). */
  allowVulnerableTags: true,
  allowedSchemes: ["http", "https", "mailto", "tel"],
  allowedSchemesByTag: {
    a: ["http", "https", "mailto", "tel"],
    img: ["http", "https"],
  },
  allowedSchemesAppliedToAttributes: ["href", "src", "cite"],
  exclusiveFilter(frame) {
    if (frame.tag !== "link") {
      return false;
    }
    const href = (frame.attribs.href ?? "").trim();
    const rel = (frame.attribs.rel ?? "").toLowerCase();
    const relTokens = rel.split(/\s+/);
    const isStylesheet = rel === "stylesheet" || relTokens.includes("stylesheet");
    const hrefOk =
      href.startsWith("https://fonts.googleapis.com/") || href.startsWith("https://fonts.gstatic.com/");
    return !(isStylesheet && hrefOk);
  },
  transformTags: {
    a: (tagName: string, attribs: Record<string, string>) => ({
      tagName,
      attribs: {
        ...attribs,
        target: "_blank",
        rel: "noopener noreferrer",
      },
    }),
  },
};

/** Sanitize published funding landing HTML for the static directory (defense in depth; mirror is sanitized at sync). */
export function sanitizeFundingLandingPageHtml(html: string): string {
  return sanitizeHtml(html ?? "", sanitizeOptions);
}
